import React, { useMemo, useState } from "react";
import type { StageConfig } from "@/core/value-objects/StageConfig";
import type { FixtureResponseDTO } from "@/shared/dto";
import { BracketConnectors, FinalConnector } from "@/frontend/components/domain/BracketConnectors";
import { formatSlotKey } from "@/frontend/utils/slotLabels";

type ViewMode = "auto" | "single" | "split";
type SideSwap = "normal" | "swapped";

interface BracketViewState {
  viewMode: ViewMode;
  reverseMatches: boolean;
  sideSwap: SideSwap;
}

export interface KnockoutBracketProps {
  stages: StageConfig[];
  mode?: ViewMode;
  consolationStage?: StageConfig;
  fixtures?: FixtureResponseDTO[];
  showControls?: boolean;
}

interface BracketMatch {
  id: string;
  matchNumber: number;
  fixture?: FixtureResponseDTO;
  homeLabel?: string;
  awayLabel?: string;
  homeFlag?: string | null;
  awayFlag?: string | null;
}

interface StageBracket {
  id: string;
  name: string;
  matches: BracketMatch[];
}

interface StageLayout {
  top: number;
  step: number;
  gap: number;
}

const TITLE_HEIGHT = 32;

function generateKnockoutBrackets(
  allStages: StageConfig[],
  fixtures?: (FixtureResponseDTO & { stageOrder?: number })[],
): StageBracket[] {
  const knockoutStages = allStages.filter(s => s.type === 'KNOCKOUT' && !s.isConsolation);
  const firstMappingStage = knockoutStages[0];
  const mappings = firstMappingStage?.mappings;

  return knockoutStages.map((stage) => {
    let matchCount = 0;
    const previousStage = allStages.find(s => s.order === stage.order - 1);
    if (previousStage) {
      matchCount = Math.floor(previousStage.progression.promotedCount / 2);
    }
    if (matchCount === 0 && stage.structure?.knockoutMatchesCount) {
      matchCount = stage.structure.knockoutMatchesCount;
    }
    if (matchCount === 0) {
      const name = stage.name.toLowerCase();
      if (name.includes("dieciseisavos")) matchCount = 16;
      else if (name.includes("octavos")) matchCount = 8;
      else if (name.includes("cuartos")) matchCount = 4;
      else if (name.includes("semi")) matchCount = 2;
      else if (name.includes("final") || name.includes("tercer")) matchCount = 1;
    }

    let stageFixtures = fixtures
      ? fixtures.filter((f) => f.stageOrder === stage.order)
      : [];
    stageFixtures = stageFixtures.sort(
      (a, b) => (a.stageMatchOrder ?? 0) - (b.stageMatchOrder ?? 0),
    );

    // si tenemos mapeos para esta etapa, comprobar si hay slots sin partido
    if (mappings && mappings.length && stage === firstMappingStage) {
      const presentSlots = stageFixtures
        .map((f) => f.homeSlotKey || f.awaySlotKey)
        .filter(Boolean) as string[];
      const missing = mappings
        .map((m) => m.slot)
        .filter((slot) => !presentSlots.includes(slot));
      if (missing.length) {
        console.warn(`Slots sin fixture para etapa ${stage.name}: ${missing.join(", ")}`);
      }
    }

    const matches: BracketMatch[] = Array.from({ length: matchCount }, (_, j) => {
      const fixture = stageFixtures[j];
      let homeLabel = "Por definir";
      let awayLabel = "Por definir";
      let homeFlag: string | null | undefined = undefined;
      let awayFlag: string | null | undefined = undefined;

      if (fixture) {
        homeLabel = fixture.homeTeam?.name || formatSlotKey(fixture.homeSlotKey, mappings) || "Por definir";
        awayLabel = fixture.awayTeam?.name || formatSlotKey(fixture.awaySlotKey, mappings) || "Por definir";
        homeFlag = fixture.homeTeam?.country?.flag;
        awayFlag = fixture.awayTeam?.country?.flag;
      }
      const number = fixture?.stageMatchOrder ?? (j + 1);
      return {
        id: `match-${stage.order}-${j + 1}`,
        matchNumber: number,
        fixture,
        homeLabel,
        awayLabel,
        homeFlag,
        awayFlag,
      };
    });

    return {
      id: `stage-${stage.order}`,
      name: stage.name,
      matches,
    };
  });
}

function renderMatchCard(match: BracketMatch) {
  const fixture = match.fixture;
  const homeFlag = fixture?.homeTeam?.country?.flag || match.homeFlag;
  const awayFlag = fixture?.awayTeam?.country?.flag || match.awayFlag;
  const homeLabel =
    fixture?.homeTeam?.name || match.homeLabel || "Por definir";
  const awayLabel =
    fixture?.awayTeam?.name || match.awayLabel || "Por definir";

  const homeWinner = fixture && fixture.winnerId === fixture.homeParticipantId && fixture.winnerId !== null && fixture.homeParticipantId !== null;
  const awayWinner = fixture && fixture.winnerId === fixture.awayParticipantId && fixture.winnerId !== null && fixture.awayParticipantId !== null;

  const hasHomeOrigin = fixture?.homeSlotParentId != null;
  const hasAwayOrigin = fixture?.awaySlotParentId != null;

  return (
    <div key={match.id} className="bg-white border border-gray-300 rounded-md px-2 py-2">
      <div className="text-[10px] text-gray-500 mb-1">Partido {match.matchNumber}</div>
      <div
        className={
          "text-sm h-6 flex items-center px-1 border border-gray-200 rounded-sm" +
          (homeWinner ? " bg-green-50 font-semibold" : "")
        }
      >
        {hasHomeOrigin && (
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-1 flex-shrink-0" title="Ganador de partido anterior" />
        )}
        {homeFlag && (
          <img
            src={homeFlag}
            alt=""
            className="w-4 h-3 object-cover rounded mr-1"
          />
        )}
        <span className="truncate">{homeLabel}</span>
      </div>
      <div className="h-1" />
      <div
        className={
          "text-sm h-6 flex items-center px-1 border border-gray-200 rounded-sm" +
          (awayWinner ? " bg-green-50 font-semibold" : "")
        }
      >
        {hasAwayOrigin && (
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-1 flex-shrink-0" title="Ganador de partido anterior" />
        )}
        {awayFlag && (
          <img
            src={awayFlag}
            alt=""
            className="w-4 h-3 object-cover rounded mr-1"
          />
        )}
        <span className="truncate">{awayLabel}</span>
      </div>
    </div>
  );
}

function buildLayouts(
  brackets: StageBracket[],
  matchHeight: number,
  baseGap: number,
  baseStep: number,
) {
  const layouts: StageLayout[] = brackets.map((_, index) => {
    const step = baseStep * Math.pow(2, index);
    const top = (baseStep * (Math.pow(2, index) - 1)) / 2;
    const gap = Math.max(step - matchHeight, baseGap);
    return { top, step, gap };
  });

  const firstCount = brackets[0]?.matches.length ?? 0;
  const height = firstCount > 0 ? firstCount * matchHeight + (firstCount - 1) * baseGap : 0;

  return { layouts, height };
}

/** Invierte el orden de los partidos en cada etapa */
function reorderStageMatches(brackets: StageBracket[], reverse: boolean): StageBracket[] {
  if (!reverse) return brackets;
  return brackets.map((stage) => ({
    ...stage,
    matches: [...stage.matches].reverse().map((m, idx) => ({
      ...m,
      matchNumber: idx + 1,
    })),
  }));
}

/** Divide brackets en mitad izquierda y derecha para vista split */
function splitBracketStages(
  branchStages: StageBracket[],
  swapped: boolean,
): { leftBrackets: StageBracket[]; rightBrackets: StageBracket[] } {
  const leftBrackets = branchStages.map((stage) => {
    const halfCount = Math.max(1, Math.floor(stage.matches.length / 2));
    const startIdx = swapped ? halfCount : 0;
    return {
      ...stage,
      id: `${stage.id}-left`,
      matches: stage.matches.slice(startIdx, startIdx + halfCount).map((m, j) => ({
        ...m,
        id: `${stage.id}-left-${j}`,
        matchNumber: j + 1,
      })),
    };
  });

  const rightBrackets = branchStages.map((stage) => {
    const halfCount = Math.max(1, Math.floor(stage.matches.length / 2));
    const startIdx = swapped ? 0 : halfCount;
    return {
      ...stage,
      id: `${stage.id}-right`,
      matches: stage.matches.slice(startIdx, startIdx + halfCount).map((m, j) => ({
        ...m,
        id: `${stage.id}-right-${j}`,
        matchNumber: halfCount + j + 1,
      })),
    };
  });

  return { leftBrackets, rightBrackets };
}

interface ViewControlsProps {
  viewState: BracketViewState;
  onViewStateChange: (state: BracketViewState) => void;
  canSplit: boolean;
}

function ViewControls({ viewState, onViewStateChange, canSplit }: ViewControlsProps) {
  const btnBase = "px-3 py-1.5 text-xs rounded border transition-colors";
  const btnActive = "bg-blue-600 text-white border-blue-600";
  const btnInactive = "bg-white text-gray-700 border-gray-300 hover:bg-gray-50";

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
      {/* Vista mode */}
      <div className="flex items-center gap-1">
        <span className="text-xs font-medium text-gray-600 mr-1">Vista:</span>
        <button
          className={`${btnBase} ${viewState.viewMode === "single" ? btnActive : btnInactive}`}
          onClick={() => onViewStateChange({ ...viewState, viewMode: "single" })}
        >
          Única
        </button>
        {canSplit && (
          <button
            className={`${btnBase} ${viewState.viewMode === "split" ? btnActive : btnInactive}`}
            onClick={() => onViewStateChange({ ...viewState, viewMode: "split" })}
          >
            Dividida
          </button>
        )}
        <button
          className={`${btnBase} ${viewState.viewMode === "auto" ? btnActive : btnInactive}`}
          onClick={() => onViewStateChange({ ...viewState, viewMode: "auto" })}
        >
          Auto
        </button>
      </div>

      {/* Separador */}
      <div className="h-6 w-px bg-gray-300 mx-1" />

      {/* Invertir partidos */}
      <button
        className={`${btnBase} ${viewState.reverseMatches ? btnActive : btnInactive}`}
        onClick={() => onViewStateChange({ ...viewState, reverseMatches: !viewState.reverseMatches })}
        title="Invierte el orden de los partidos en cada ronda"
      >
        {viewState.reverseMatches ? "↑ Normal" : "↓ Invertir"}
      </button>

      {/* Intercambiar lados (solo en split) */}
      {(viewState.viewMode === "split" || (viewState.viewMode === "auto" && canSplit)) && (
        <>
          <div className="h-6 w-px bg-gray-300 mx-1" />
          <button
            className={`${btnBase} ${viewState.sideSwap === "swapped" ? btnActive : btnInactive}`}
            onClick={() => onViewStateChange({ 
              ...viewState, 
              sideSwap: viewState.sideSwap === "normal" ? "swapped" : "normal" 
            })}
            title="Intercambia qué mitad de la llave va a la izquierda o derecha"
          >
            {viewState.sideSwap === "swapped" ? "⇄ Restaurar" : "⇄ Intercambiar"}
          </button>
        </>
      )}
    </div>
  );
}

interface BracketSectionProps {
  brackets: StageBracket[];
  matchHeight: number;
  baseGap: number;
  baseStep: number;
  direction?: "ltr" | "rtl";
}

function BracketSection({ brackets, matchHeight, baseGap, baseStep, direction = "ltr" }: BracketSectionProps) {
  const { layouts, height } = useMemo(
    () => buildLayouts(brackets, matchHeight, baseGap, baseStep),
    [brackets, matchHeight, baseGap, baseStep],
  );

  if (!brackets.length || !height) {
    return null;
  }

  const displayBrackets = direction === "rtl" ? [...brackets].reverse() : brackets;
  const displayLayouts = direction === "rtl" ? [...layouts].reverse() : layouts;

  return (
    <div className="relative flex items-start" style={{ height: height + TITLE_HEIGHT }}>
      {displayBrackets.map((bracket, index) => {
        const layout = displayLayouts[index]!;
        
        const nextDisplayIndex = index + 1;
        const nextBracket = displayBrackets[nextDisplayIndex];
        const nextLayout = displayLayouts[nextDisplayIndex];

        const showConnector = nextBracket && nextLayout;

        // En RTL: visualmente va de menos partidos (izq) a más partidos (der)
        // El SVG en RTL dibuja líneas de derecha a izquierda
        // "from" = el lado con más partidos (nextBracket en RTL)
        // "to" = el lado con menos partidos (bracket actual en RTL)
        const connectorFrom = direction === "rtl"
          ? { count: nextBracket?.matches.length ?? 0, top: (nextLayout?.top ?? 0) + TITLE_HEIGHT, step: nextLayout?.step ?? 0, itemHeight: matchHeight, gap: nextLayout?.gap ?? 0 }
          : { count: bracket.matches.length, top: layout.top + TITLE_HEIGHT, step: layout.step, itemHeight: matchHeight, gap: layout.gap };
        
        const connectorTo = direction === "rtl"
          ? { count: bracket.matches.length, top: layout.top + TITLE_HEIGHT, step: layout.step, itemHeight: matchHeight, gap: layout.gap }
          : { count: nextBracket?.matches.length ?? 0, top: (nextLayout?.top ?? 0) + TITLE_HEIGHT, step: nextLayout?.step ?? 0, itemHeight: matchHeight, gap: nextLayout?.gap ?? 0 };

        return (
          <React.Fragment key={bracket.id}>
            <div className="w-40 flex-shrink-0" style={{ marginTop: layout.top }}>
              <div className="font-bold mb-3 text-center">{bracket.name}</div>
              <div className="flex flex-col" style={{ gap: layout.gap }}>
                {bracket.matches.map((match) => renderMatchCard(match))}
              </div>
            </div>

            {showConnector && (
              <BracketConnectors
                from={connectorFrom}
                to={connectorTo}
                height={height + TITLE_HEIGHT}
                direction={direction}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export const KnockoutBracket: React.FC<KnockoutBracketProps> = ({ stages, mode = "auto", consolationStage, fixtures, showControls = true }) => {
  const [viewState, setViewState] = useState<BracketViewState>({
    viewMode: mode,
    reverseMatches: false,
    sideSwap: "normal",
  });

  const baseBrackets = useMemo(
    () => generateKnockoutBrackets(stages, fixtures),
    [stages, fixtures],
  );

  // Aplicar reordenamiento de partidos
  const brackets = useMemo(
    () => reorderStageMatches(baseBrackets, viewState.reverseMatches),
    [baseBrackets, viewState.reverseMatches],
  );

  const matchHeight = 86;
  const baseGap = 14;
  const baseStep = matchHeight + baseGap;

  if (!brackets.length) {
    return null;
  }

  const firstRoundMatches = brackets[0]?.matches.length ?? 0;
  const canSplit = brackets.length >= 2 && firstRoundMatches >= 2;
  
  const resolvedMode: "single" | "split" =
    viewState.viewMode === "auto" 
      ? (firstRoundMatches >= 8 ? "split" : "single") 
      : viewState.viewMode;

  if (resolvedMode === "split" && brackets.length >= 2) {
    const centerBracket = brackets[brackets.length - 1]!;
    const branchStages = brackets.slice(0, -1);

    // Usar helper para dividir con soporte de swap
    const { leftBrackets, rightBrackets } = splitBracketStages(
      branchStages, 
      viewState.sideSwap === "swapped"
    );

    // Calcular altura total basada en la primera ronda
    const { height: leftHeight } = buildLayouts(leftBrackets, matchHeight, baseGap, baseStep);
    const totalHeight = leftHeight + TITLE_HEIGHT;

    // Posición Y de la última etapa de cada lado (semifinal) para conectar a la final
    const lastLeftLayout = buildLayouts(leftBrackets, matchHeight, baseGap, baseStep).layouts;
    const lastRightLayout = buildLayouts(rightBrackets, matchHeight, baseGap, baseStep).layouts;
    
    const lastLeftStageLayout = lastLeftLayout[lastLeftLayout.length - 1];
    const lastRightStageLayout = lastRightLayout[lastRightLayout.length - 1];
    
    // Centro Y de la semifinal izquierda y derecha
    const leftSemiY = lastLeftStageLayout 
      ? lastLeftStageLayout.top + TITLE_HEIGHT + matchHeight / 2 
      : totalHeight / 2;
    const rightSemiY = lastRightStageLayout 
      ? lastRightStageLayout.top + TITLE_HEIGHT + matchHeight / 2 
      : totalHeight / 2;
    
    // Centro Y de la final (centrada verticalmente)
    const finalY = totalHeight / 2;

    // Buscar fixture del tercer puesto si existe
    const consolationFixture: BracketMatch | null = consolationStage
      ? (() => {
          const consolationFixtures = (fixtures as (FixtureResponseDTO & { stageOrder?: number })[] | undefined)?.filter(f => f.stageOrder === consolationStage.order) ?? [];
          const fix = consolationFixtures[0];
          if (fix) {
            return {
              id: "consolation-match",
              matchNumber: 1,
              fixture: fix,
              homeLabel: fix.homeTeam?.name || "Por definir",
              awayLabel: fix.awayTeam?.name || "Por definir",
              homeFlag: fix.homeTeam?.country?.flag,
              awayFlag: fix.awayTeam?.country?.flag,
            };
          }
          return null;
        })()
      : null;

    return (
      <div className="overflow-x-auto">
        <div className="py-4" style={{ minWidth: "max-content" }}>
          {showControls && (
            <ViewControls
              viewState={viewState}
              onViewStateChange={setViewState}
              canSplit={canSplit}
            />
          )}
          <div className="flex items-start">
            {/* Llave izquierda: flujo normal ltr */}
            <BracketSection
              brackets={leftBrackets}
              matchHeight={matchHeight}
              baseGap={baseGap}
              baseStep={baseStep}
              direction="ltr"
            />

            {/* Conector de semi izquierda a final */}
            <FinalConnector
              fromY={leftSemiY}
              toY={finalY}
              height={totalHeight}
              direction="ltr"
            />

            {/* Final centrada y tercer puesto posicionado absolutamente abajo */}
            <div 
              className="w-40 flex-shrink-0 flex flex-col relative"
              style={{ height: totalHeight, justifyContent: "center" }}
            >
              <div>
                <div className="font-bold mb-3 text-center">{centerBracket.name}</div>
                {renderMatchCard(centerBracket.matches[0] ?? {
                  id: `${centerBracket.id}-final`,
                  matchNumber: 1,
                  homeLabel: "Por definir",
                  awayLabel: "Por definir",
                })}
              </div>
              {consolationStage && (
                <div 
                  className="absolute w-full"
                  style={{ top: totalHeight / 2 + matchHeight / 2 + 64 }}
                >
                  <div className="font-bold mb-3 text-center">{consolationStage.name}</div>
                  {renderMatchCard(consolationFixture ?? {
                    id: `consolation-match`,
                    matchNumber: 1,
                    homeLabel: "Por definir",
                    awayLabel: "Por definir",
                  })}
                </div>
              )}
            </div>

            {/* Conector de final a semi derecha */}
            <FinalConnector
              fromY={rightSemiY}
              toY={finalY}
              height={totalHeight}
              direction="rtl"
            />

            {/* Llave derecha: flujo invertido rtl */}
            <BracketSection
              brackets={rightBrackets}
              matchHeight={matchHeight}
              baseGap={baseGap}
              baseStep={baseStep}
              direction="rtl"
            />
          </div>
          <div style={{ height: "4rem" }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="py-4" style={{ minWidth: "max-content" }}>
        {showControls && (
          <ViewControls
            viewState={viewState}
            onViewStateChange={setViewState}
            canSplit={canSplit}
          />
        )}
        <div className="relative flex items-start">
          <BracketSection
            brackets={brackets}
            matchHeight={matchHeight}
            baseGap={baseGap}
            baseStep={baseStep}
          />
          {consolationStage && (() => {
            const lastLayout = buildLayouts(brackets, matchHeight, baseGap, baseStep).layouts[brackets.length - 1];
            const finalTop = lastLayout?.top ?? 0;
            const consolationTop = finalTop + TITLE_HEIGHT + matchHeight + 64;
            
            // Calcular posición horizontal: después de todas las columnas de brackets
            const totalBracketWidth = brackets.length * 160 + (brackets.length - 1) * 56;
            
            // Buscar fixture del tercer puesto
            const consolationFixtures = (fixtures as (FixtureResponseDTO & { stageOrder?: number })[] | undefined)?.filter(f => f.stageOrder === consolationStage.order) ?? [];
            const consolationFix = consolationFixtures[0];
            const consolationMatch: BracketMatch = consolationFix
              ? {
                  id: "consolation-match",
                  matchNumber: 1,
                  fixture: consolationFix,
                  homeLabel: consolationFix.homeTeam?.name || "Por definir",
                  awayLabel: consolationFix.awayTeam?.name || "Por definir",
                  homeFlag: consolationFix.homeTeam?.country?.flag,
                  awayFlag: consolationFix.awayTeam?.country?.flag,
                }
              : {
                  id: "consolation-match",
                  matchNumber: 1,
                  homeLabel: "Por definir",
                  awayLabel: "Por definir",
                };

            return (
              <div 
                className="absolute w-40" 
                style={{ 
                  top: consolationTop,
                  left: totalBracketWidth - 160
                }}
              >
                <div className="font-bold mb-3 text-center">{consolationStage.name}</div>
                {renderMatchCard(consolationMatch)}
              </div>
            );
          })()}
        </div>
        <div style={{ height: "2rem" }} />
      </div>
    </div>
  );
};
